import { Activity, BellOff, LayoutDashboard, Plug, Send, Server, Siren, Users, Workflow, type LucideIcon } from "lucide-react";
import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { MenuEntry } from "../lib/types";

/**
 * One route manifest for the sidebar and the router (§8.2), replacing the two
 * copies of the visibility rule supervisor keeps in CorePage and RouterFile.
 */
export type RouteDef = {
  path: string;
  /** Present → listed in the sidebar. */
  label?: string;
  icon?: LucideIcon;
  group?: "Observe" | "Configure" | "Connect" | "Organization";
  /** Visible when ANY of these tables is in the role's menu with showInMenu. */
  tables?: string[];
  element: LazyExoticComponent<ComponentType>;
  /** Detail routes share their parent's gate. */
  children?: { path: string; element: LazyExoticComponent<ComponentType> }[];
};

export const ROUTES: RouteDef[] = [
  { path: "/", label: "Overview", icon: Activity, group: "Observe", element: lazy(() => import("../pages/OverviewPage")) },
  {
    path: "/alerts",
    label: "Alerts",
    icon: Siren,
    group: "Observe",
    tables: ["alert"],
    element: lazy(() => import("../pages/AlertsPage")),
    children: [{ path: ":id", element: lazy(() => import("../pages/AlertDetailPage")) }],
  },
  {
    path: "/status-pages",
    label: "Status pages",
    icon: LayoutDashboard,
    group: "Observe",
    tables: ["statuspage"],
    element: lazy(() => import("../pages/StatusPagesPage")),
    children: [{ path: ":id", element: lazy(() => import("../pages/StatusPageDetailPage")) }],
  },
  { path: "/silences", label: "Silences", icon: BellOff, group: "Observe", tables: ["silence"], element: lazy(() => import("../pages/SilencesPage")) },
  {
    path: "/monitors",
    label: "Monitors",
    icon: Workflow,
    group: "Configure",
    tables: ["monitor"],
    element: lazy(() => import("../pages/MonitorsPage")),
    children: [
      { path: "new/flow", element: lazy(() => import("../pages/FlowEditorPage")) },
      { path: ":id/flow", element: lazy(() => import("../pages/FlowEditorPage")) },
      { path: ":id", element: lazy(() => import("../pages/MonitorDetailPage")) },
    ],
  },
  {
    path: "/notifications",
    label: "Notifications",
    icon: Send,
    group: "Configure",
    tables: ["notificationchannel", "notificationpolicy"],
    element: lazy(() => import("../pages/NotificationsPage")),
  },
  { path: "/connections", label: "Connections", icon: Plug, group: "Connect", tables: ["connection"], element: lazy(() => import("../pages/ConnectionsPage")) },
  { path: "/runners", label: "Runners", icon: Server, group: "Connect", tables: ["runnergroup"], element: lazy(() => import("../pages/RunnersPage")) },
  { path: "/team", label: "Team", icon: Users, group: "Organization", tables: ["membership"], element: lazy(() => import("../pages/TeamPage")) },
];

export const canSee = (route: RouteDef, menu: Record<string, MenuEntry>) => !route.tables || route.tables.some((t) => menu[t]?.showInMenu === true);
